// ===========================================================================
// Industry verticals — landing pages with executive intake, sized for SEO.
// ===========================================================================

export const INDUSTRY_PAGES = [
  {
    slug: "manufacturing",
    name: "Manufacturing",
    eyebrow: "Industrial Manufacturing",
    headline: "Re-architect the plant, the network, and the value chain.",
    lead:
      "From discrete production to process operations, AlchamisGroup advises manufacturing CEOs and operating partners on AI-led modernisation, PLM consolidation, and operational excellence.",
    hero: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?auto=format&fit=crop&w=1800&q=80",
    pressures: [
      "Margin pressure from input volatility and capital discipline.",
      "Aging ERP / MES / PLM estates that block productivity gains.",
      "Quality and audit risk across global supplier networks.",
      "Workforce shortage requiring AI-augmented operations.",
    ],
    capabilities: [
      "Operating model design across plants and regions.",
      "Quality system consolidation with Alchamis PLM.",
      "AI-augmented manufacturing operations and predictive maintenance.",
      "Supplier network governance and audit-ready evidence.",
    ],
    outcomes: [
      { value: "30%", label: "Lower scrap and rework" },
      { value: "42%", label: "Faster engineering change" },
      { value: "$94M", label: "Avg annual run-rate value released" },
    ],
    services: ["operational-excellence", "quality-management", "technology-modernization", "workflow-automation"],
    platforms: ["alchamis-plm"],
    seoTitle: "Manufacturing Consulting & PLM — AlchamisGroup",
    seoDescription:
      "Operating model, PLM consolidation, and AI-led modernisation for industrial manufacturers. Quality, supplier, and plant programmes delivered partner-first.",
  },
  {
    slug: "automotive",
    name: "Automotive",
    eyebrow: "Automotive & Mobility",
    headline: "Engineered for the EV, software-defined-vehicle era.",
    lead:
      "OEMs and tier suppliers reshaping platforms, software, and supplier networks for the next product cycle. AlchamisGroup brings PLM and AI engineering depth to the boardroom.",
    hero: "https://images.unsplash.com/photo-1580273916550-e323be2ae537?auto=format&fit=crop&w=1800&q=80",
    pressures: [
      "Software-defined platforms outpacing legacy product organisations.",
      "EV and battery investment shifting engineering priorities.",
      "Supplier consolidation and regulatory complexity (UNECE, GDPR).",
      "Customer experience expectations re-shaped by tech-native peers.",
    ],
    capabilities: [
      "Product platform strategy and PLM consolidation.",
      "Software-defined-vehicle engineering operating model.",
      "AI-led quality, warranty, and supplier intelligence.",
      "Customer-experience and dealer ecosystem modernisation.",
    ],
    outcomes: [
      { value: "−18%", label: "Engineering change lead time" },
      { value: "+24%", label: "First-time-right quality" },
      { value: "+11pts", label: "Owner NPS uplift" },
    ],
    services: ["digital-transformation", "quality-management", "ai-strategy", "customer-experience"],
    platforms: ["alchamis-plm", "alchamis-crm"],
    seoTitle: "Automotive Consulting — AlchamisGroup",
    seoDescription:
      "Platform, software-defined vehicle, PLM, and AI consulting for OEMs and tier-one automotive suppliers. Engineered for the next product cycle.",
  },
  {
    slug: "aerospace-defense",
    name: "Aerospace & Defense",
    eyebrow: "Aerospace & Defense",
    headline: "Mission-grade modernisation under regulatory discipline.",
    lead:
      "Programmes for OEMs, tier suppliers, and primes — modernising engineering, supply chain, and compliance with the discipline regulators expect and operators demand.",
    hero: "https://images.unsplash.com/photo-1542213486-d8ce62e1f70a?auto=format&fit=crop&w=1800&q=80",
    pressures: [
      "AS9100 / EASA / FAA programmes under continuous audit.",
      "Engineering data fragmentation across multi-decade product lifecycles.",
      "Defense ITAR / CMMC compliance and supplier risk.",
      "Adoption of AI in mission-critical operating environments.",
    ],
    capabilities: [
      "PLM and configuration management modernisation.",
      "Supplier quality and audit-grade evidence chains.",
      "AI in mission-grade decision support with governance.",
      "Compliance instrumentation (AS9100, CMMC, ITAR).",
    ],
    outcomes: [
      { value: "70%", label: "Audit prep time reduction" },
      { value: "31%", label: "Lower quality incidents" },
      { value: "100%", label: "Regulator readiness sustained" },
    ],
    services: ["quality-management", "compliance-governance", "enterprise-architecture", "ai-strategy"],
    platforms: ["alchamis-plm"],
    seoTitle: "Aerospace & Defense Consulting — AlchamisGroup",
    seoDescription:
      "Mission-grade PLM, quality, and AI consulting for aerospace and defense primes, OEMs, and tier suppliers. Built for AS9100, ITAR, CMMC.",
  },
  {
    slug: "healthcare",
    name: "Healthcare & Life Sciences",
    eyebrow: "Healthcare & Life Sciences",
    headline: "Clinical-grade transformation for systems, payers, and MedTech.",
    lead:
      "Hospital systems, MedTech, biotech, and payers turning to AlchamisGroup for AI-led operations, quality systems, and patient-experience programmes.",
    hero: "https://images.unsplash.com/photo-1538108149393-fbbd81895907?auto=format&fit=crop&w=1800&q=80",
    pressures: [
      "Clinical operations under workforce and margin pressure.",
      "FDA 21 CFR Part 11, EU MDR, and ISO 13485 programmes.",
      "Patient experience as the new competitive differentiator.",
      "AI adoption with HIPAA-grade governance and provenance.",
    ],
    capabilities: [
      "Clinical and back-office operations transformation.",
      "MedTech quality systems and regulatory readiness.",
      "Patient experience programmes and service operations.",
      "Generative AI with HIPAA-aligned governance.",
    ],
    outcomes: [
      { value: "−22%", label: "Cost-to-serve in service ops" },
      { value: "+18pts", label: "Patient experience score" },
      { value: "100%", label: "Audit readiness sustained" },
    ],
    services: ["operational-excellence", "quality-management", "compliance-governance", "ai-strategy"],
    platforms: ["alchamis-plm", "alchamis-crm"],
    seoTitle: "Healthcare & Life Sciences Consulting — AlchamisGroup",
    seoDescription:
      "Clinical-grade AI consulting, MedTech quality systems, and patient experience programmes for hospitals, biotech, MedTech, and payers.",
  },
  {
    slug: "financial-services",
    name: "Financial Services",
    eyebrow: "Banking, Asset Management & Insurance",
    headline: "Revenue, risk, and AI under board-level discipline.",
    lead:
      "Banks, insurers, asset managers, and market-infrastructure operators engaging AlchamisGroup for AI strategy, customer-experience modernisation, and risk integration.",
    hero: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1800&q=80",
    pressures: [
      "AI fiduciary responsibility at the board level.",
      "Customer-experience expectations set by neobanks and platforms.",
      "Integrated risk reporting (AI, cyber, regulatory, operational).",
      "Core modernisation and cost-to-serve pressure.",
    ],
    capabilities: [
      "AI portfolio design and governance for boards.",
      "Customer experience and service-ops re-architecture.",
      "Risk operating model and continuous assurance.",
      "Core technology modernisation and architecture.",
    ],
    outcomes: [
      { value: "$180M", label: "AI operating leverage projection" },
      { value: "3.4×", label: "Pipeline visibility uplift" },
      { value: "−27%", label: "Revenue leakage reduction" },
    ],
    services: ["ai-strategy", "customer-experience", "risk-management", "technology-modernization"],
    platforms: ["alchamis-crm"],
    seoTitle: "Financial Services Consulting — AlchamisGroup",
    seoDescription:
      "AI strategy, CX modernisation, risk operating model, and core technology consulting for banks, insurers, and asset managers.",
  },
  {
    slug: "government",
    name: "Government",
    eyebrow: "Public Sector & Government",
    headline: "Mission outcomes through institutional discipline.",
    lead:
      "Federal, state, and sovereign operators engaging AlchamisGroup for AI adoption, operating-model design, and compliance programmes engineered for public scrutiny.",
    hero: "https://images.unsplash.com/photo-1577562116089-1eecfd5be9c1?auto=format&fit=crop&w=1800&q=80",
    pressures: [
      "AI adoption under public scrutiny and procurement discipline.",
      "Mission-critical operations with finite capital and talent.",
      "FedRAMP / CMMC / SOC 2 compliance programmes.",
      "Citizen experience expectations from private-sector peers.",
    ],
    capabilities: [
      "AI strategy and governance for public-sector boards.",
      "Citizen-experience programmes and service operations.",
      "Compliance instrumentation (FedRAMP, CMMC, SOC 2).",
      "Mission-grade workforce and capability programmes.",
    ],
    outcomes: [
      { value: "+24%", label: "Citizen satisfaction uplift" },
      { value: "−30%", label: "Time-to-procure" },
      { value: "100%", label: "Audit-grade evidence chain" },
    ],
    services: ["ai-strategy", "compliance-governance", "operational-excellence", "customer-experience"],
    platforms: ["alchamis-crm"],
    seoTitle: "Government Consulting — AlchamisGroup",
    seoDescription:
      "AI adoption, mission operations, and compliance programmes for federal, state, and sovereign government clients.",
  },
  {
    slug: "education",
    name: "Education",
    eyebrow: "Higher Education & K-12",
    headline: "From classroom to operating model.",
    lead:
      "Higher education, K-12 networks, and education operators modernising operations, student experience, and AI policy — partner-led across academic and administrative functions.",
    hero: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1800&q=80",
    pressures: [
      "Enrollment competition and changing student expectations.",
      "AI policy and academic-integrity governance.",
      "Administrative cost-to-serve under enrolment pressure.",
      "Workforce and capability shifts toward digital-first delivery.",
    ],
    capabilities: [
      "Operating-model design across academic and administrative functions.",
      "Student experience and CRM modernisation.",
      "AI policy and governance for academic integrity.",
      "Workforce and capability programmes.",
    ],
    outcomes: [
      { value: "−18%", label: "Administrative cost-to-serve" },
      { value: "+14pts", label: "Student satisfaction" },
      { value: "100%", label: "Academic AI governance coverage" },
    ],
    services: ["operational-excellence", "ai-strategy", "customer-experience", "organizational-change"],
    platforms: ["alchamis-crm"],
    seoTitle: "Education Consulting — AlchamisGroup",
    seoDescription:
      "Operating-model, student-experience, and AI policy consulting for higher education and K-12 networks.",
  },
  {
    slug: "childcare",
    name: "Childcare",
    eyebrow: "Early Years & Childcare Networks",
    headline: "Operating discipline for high-trust early-years networks.",
    lead:
      "Childcare networks and early-years operators bringing institutional discipline to enrolment, compliance, and family engagement — with the support of Alouvya.",
    hero: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=1800&q=80",
    pressures: [
      "Operating across many centres with limited central infrastructure.",
      "Safeguarding, ratios, and inspection evidence.",
      "Funding regimes and billing complexity.",
      "Family-experience expectations and digital communication.",
    ],
    capabilities: [
      "Multi-centre operating model and management cadence.",
      "Compliance and safeguarding programmes.",
      "Family engagement and parent-experience design.",
      "Finance, funding, and revenue analytics with Alouvya.",
    ],
    outcomes: [
      { value: "11 hrs/wk", label: "Saved per centre" },
      { value: "94 NPS", label: "Parent satisfaction" },
      { value: "100%", label: "Inspection readiness" },
    ],
    services: ["operational-excellence", "compliance-governance", "customer-experience"],
    platforms: ["alouvya"],
    seoTitle: "Childcare Network Consulting & Alouvya — AlchamisGroup",
    seoDescription:
      "Operating model, compliance, and family-experience consulting for childcare networks. Supported by Alouvya, our childcare platform.",
  },
  {
    slug: "technology",
    name: "Technology",
    eyebrow: "Software, Platforms & Telecom",
    headline: "AI-first product organisations, engineered for scale.",
    lead:
      "Software and platform companies, telecoms, and infrastructure operators turning to AlchamisGroup for AI strategy, product organisation, and revenue intelligence.",
    hero: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1800&q=80",
    pressures: [
      "AI repositioning at the product and brand level.",
      "Revenue operations and pipeline integrity as growth slows.",
      "Cost discipline and run-cost optimisation.",
      "Workforce and engineering productivity.",
    ],
    capabilities: [
      "AI product strategy and competitive positioning.",
      "Revenue operations modernisation with Alchamis CRM.",
      "Engineering productivity and platform investment.",
      "Workforce and AI augmentation programmes.",
    ],
    outcomes: [
      { value: "+22%", label: "Engineering productivity" },
      { value: "+34%", label: "Pipeline accuracy" },
      { value: "−19%", label: "Run-cost reduction" },
    ],
    services: ["ai-strategy", "digital-transformation", "business-intelligence", "customer-experience"],
    platforms: ["alchamis-crm"],
    seoTitle: "Technology Sector Consulting — AlchamisGroup",
    seoDescription:
      "AI strategy, revenue operations, and engineering productivity consulting for software, platform, telecom, and infrastructure firms.",
  },
  {
    slug: "hospitality",
    name: "Hospitality",
    eyebrow: "Luxury Hospitality & Lifestyle",
    headline: "Guest experience as a balance-sheet asset.",
    lead:
      "Luxury hotels, member networks, and lifestyle brands modernising guest experience, revenue operations, and AI personalisation — without compromising brand discipline.",
    hero: "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=1800&q=80",
    pressures: [
      "Guest expectations shaped by digital-native brands.",
      "Revenue management and loyalty programme complexity.",
      "AI personalisation aligned to luxury brand discipline.",
      "Workforce and service-operations productivity.",
    ],
    capabilities: [
      "Guest experience strategy and service operations.",
      "Revenue intelligence and loyalty programme design.",
      "AI personalisation with brand-grade governance.",
      "Workforce productivity and capability programmes.",
    ],
    outcomes: [
      { value: "+19%", label: "RevPAR uplift" },
      { value: "+27pts", label: "Guest experience score" },
      { value: "−16%", label: "Service-ops cost-to-serve" },
    ],
    services: ["customer-experience", "ai-strategy", "operational-excellence", "business-intelligence"],
    platforms: ["alchamis-crm", "datendog"],
    seoTitle: "Luxury Hospitality Consulting — AlchamisGroup",
    seoDescription:
      "Guest experience, revenue intelligence, and AI personalisation consulting for luxury hospitality and lifestyle brands.",
  },
];
